import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { SearchAddon } from '@xterm/addon-search';
import { io } from 'socket.io-client';
import { useAuth } from '../../../contexts/AuthContext';
import '@xterm/xterm/css/xterm.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.ilouli.com';
const THEME_STORAGE_KEY = 'terminal-theme';

// 테마 아이콘 컴포넌트
const SunIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const MoonIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
    />
  </svg>
);

const TerminalPage = () => {
  const { token } = useAuth();
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [connectionModal, setConnectionModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarTab, setSidebarTab] = useState('connections'); // connections, keys
  const [savedConnections, setSavedConnections] = useState([]);
  const [sshKeys, setSshKeys] = useState([]);
  const [theme, setTheme] = useState('light');
  const [connectionForm, setConnectionForm] = useState({
    type: 'local',
    host: '',
    port: 22,
    username: '',
    password: '',
    name: '',
    saveConnection: false
  });
  const [keyModal, setKeyModal] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: '', keyType: 'rsa' });
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const terminalsRef = useRef({});
  const socketsRef = useRef({});
  const containerRef = useRef(null);

  // 테마 초기화
  useEffect(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    const initialTheme = stored || 'light';
    setTheme(initialTheme);
  }, []);

  // 테마 적용
  useEffect(() => {
    document.documentElement.setAttribute('data-terminal-theme', theme);
  }, [theme]);

  // 테마 토글
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  }, [theme]);

  // API 헬퍼
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || '요청 실패');
    return data;
  }, [token]);

  // 저장된 연결 목록 로드
  const loadConnections = useCallback(async () => {
    try {
      const data = await apiCall('/api/terminal/connections');
      setSavedConnections(data.data || []);
    } catch (err) {
      console.error('연결 목록 로드 실패:', err);
    }
  }, [apiCall]);

  // SSH 키 목록 로드
  const loadKeys = useCallback(async () => {
    try {
      const data = await apiCall('/api/terminal/keys');
      setSshKeys(data.data || []);
    } catch (err) {
      console.error('SSH 키 로드 실패:', err);
    }
  }, [apiCall]);

  // 초기 데이터 로드
  useEffect(() => {
    if (token) {
      loadConnections();
      loadKeys();
    }
  }, [token, loadConnections, loadKeys]);

  // 새 탭 생성
  const createTab = useCallback((type = 'local', connectionInfo = null) => {
    const tabId = `tab-${Date.now()}`;
    const newTab = {
      id: tabId,
      type,
      name: connectionInfo?.name || (type === 'local' ? '로컬 터미널' : `SSH: ${connectionInfo?.host}`),
      connectionInfo,
      status: 'connecting'
    };

    setTabs(prev => [...prev, newTab]);
    setActiveTab(tabId);
    return tabId;
  }, []);

  // 터미널 초기화
  const initTerminal = useCallback((tabId, container) => {
    if (!container || terminalsRef.current[tabId]) return;

    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        selection: 'rgba(255, 255, 255, 0.3)',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#ffffff'
      }
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    const searchAddon = new SearchAddon();

    terminal.loadAddon(fitAddon);
    terminal.loadAddon(webLinksAddon);
    terminal.loadAddon(searchAddon);

    terminal.open(container);
    fitAddon.fit();

    const handleResize = () => {
      fitAddon.fit();
      const socket = socketsRef.current[tabId];
      if (socket?.connected) {
        socket.emit('resize', { cols: terminal.cols, rows: terminal.rows });
      }
    };

    window.addEventListener('resize', handleResize);

    terminalsRef.current[tabId] = {
      terminal,
      fitAddon,
      searchAddon,
      cleanup: () => {
        window.removeEventListener('resize', handleResize);
        terminal.dispose();
      }
    };

    return terminal;
  }, []);

  // Socket 연결
  const connectSocket = useCallback((tabId, tab) => {
    if (socketsRef.current[tabId]) return;

    const socket = io(`${API_URL}/terminal`, {
      auth: { token },
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      const terminal = terminalsRef.current[tabId]?.terminal;
      socket.emit('start', {
        type: tab.type,
        connectionInfo: tab.connectionInfo,
        cols: terminal?.cols || 80,
        rows: terminal?.rows || 24
      });
    });

    socket.on('data', (data) => {
      const terminal = terminalsRef.current[tabId]?.terminal;
      if (terminal) terminal.write(data);
    });

    socket.on('ready', () => {
      setTabs(prev => prev.map(t => t.id === tabId ? { ...t, status: 'connected' } : t));
    });

    socket.on('error', (err) => {
      setError(err.message);
      setTabs(prev => prev.map(t => t.id === tabId ? { ...t, status: 'error' } : t));
    });

    socket.on('disconnect', () => {
      setTabs(prev => prev.map(t => t.id === tabId ? { ...t, status: 'disconnected' } : t));
    });

    socket.on('exit', (code) => {
      const terminal = terminalsRef.current[tabId]?.terminal;
      if (terminal) terminal.writeln(`\r\n\x1b[33m[세션 종료: 코드 ${code}]\x1b[0m`);
      setTabs(prev => prev.map(t => t.id === tabId ? { ...t, status: 'exited' } : t));
    });

    socketsRef.current[tabId] = socket;

    const terminal = terminalsRef.current[tabId]?.terminal;
    if (terminal) {
      terminal.onData((data) => socket.emit('input', data));
    }
  }, [token]);

  // 탭 닫기
  const closeTab = useCallback((tabId) => {
    const socket = socketsRef.current[tabId];
    if (socket) {
      socket.disconnect();
      delete socketsRef.current[tabId];
    }

    const terminalData = terminalsRef.current[tabId];
    if (terminalData) {
      terminalData.cleanup();
      delete terminalsRef.current[tabId];
    }

    setTabs(prev => {
      const newTabs = prev.filter(t => t.id !== tabId);
      if (activeTab === tabId && newTabs.length > 0) {
        setActiveTab(newTabs[newTabs.length - 1].id);
      } else if (newTabs.length === 0) {
        setActiveTab(null);
      }
      return newTabs;
    });
  }, [activeTab]);

  // 로컬 터미널 열기
  const openLocalTerminal = useCallback(() => {
    createTab('local');
    setConnectionModal(false);
  }, [createTab]);

  // SSH 연결
  const openSSHConnection = useCallback(async () => {
    if (!connectionForm.host || !connectionForm.username) {
      setError('호스트와 사용자명을 입력해주세요.');
      return;
    }

    setIsConnecting(true);
    const connectionInfo = { ...connectionForm };

    // 연결 저장
    if (connectionForm.saveConnection && connectionForm.name) {
      try {
        await apiCall('/api/terminal/connections', {
          method: 'POST',
          body: JSON.stringify({
            name: connectionForm.name,
            host: connectionForm.host,
            port: connectionForm.port,
            username: connectionForm.username,
            auth_type: 'password'
          })
        });
        loadConnections();
      } catch (err) {
        console.error('연결 저장 실패:', err);
      }
    }

    createTab('ssh', connectionInfo);
    setConnectionModal(false);
    setConnectionForm({
      type: 'local', host: '', port: 22, username: '', password: '', name: '', saveConnection: false
    });
    setIsConnecting(false);
  }, [connectionForm, createTab, apiCall, loadConnections]);

  // 저장된 연결로 접속
  const connectFromSaved = useCallback((conn) => {
    setConnectionForm({
      type: 'ssh',
      host: conn.host,
      port: conn.port,
      username: conn.username,
      password: '',
      name: conn.name,
      saveConnection: false
    });
    setConnectionModal(true);
  }, []);

  // 연결 삭제
  const deleteConnection = useCallback(async (id) => {
    if (!window.confirm('이 연결을 삭제하시겠습니까?')) return;
    try {
      await apiCall(`/api/terminal/connections/${id}`, { method: 'DELETE' });
      loadConnections();
    } catch (err) {
      setError(err.message);
    }
  }, [apiCall, loadConnections]);

  // SSH 키 생성
  const generateKey = useCallback(async () => {
    if (!keyForm.name) {
      setError('키 이름을 입력해주세요.');
      return;
    }
    setLoading(true);
    try {
      await apiCall('/api/terminal/keys/generate', {
        method: 'POST',
        body: JSON.stringify({ name: keyForm.name, key_type: keyForm.keyType })
      });
      loadKeys();
      setKeyModal(false);
      setKeyForm({ name: '', keyType: 'rsa' });
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [keyForm, apiCall, loadKeys]);

  // SSH 키 삭제
  const deleteKey = useCallback(async (id) => {
    if (!window.confirm('이 SSH 키를 삭제하시겠습니까?')) return;
    try {
      await apiCall(`/api/terminal/keys/${id}`, { method: 'DELETE' });
      loadKeys();
    } catch (err) {
      setError(err.message);
    }
  }, [apiCall, loadKeys]);

  // 활성 탭 터미널 초기화
  useEffect(() => {
    if (!activeTab) return;
    const tab = tabs.find(t => t.id === activeTab);
    if (!tab) return;

    const timer = setTimeout(() => {
      const container = document.getElementById(`terminal-${activeTab}`);
      if (container && !terminalsRef.current[activeTab]) {
        initTerminal(activeTab, container);
        connectSocket(activeTab, tab);
      }
      const terminalData = terminalsRef.current[activeTab];
      if (terminalData?.fitAddon) terminalData.fitAddon.fit();
    }, 100);

    return () => clearTimeout(timer);
  }, [activeTab, tabs, initTerminal, connectSocket]);

  // 사이드바 토글 시 터미널 리사이즈
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab && terminalsRef.current[activeTab]?.fitAddon) {
        terminalsRef.current[activeTab].fitAddon.fit();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [sidebarOpen, activeTab]);

  // Cleanup
  useEffect(() => {
    const sockets = socketsRef.current;
    const terminals = terminalsRef.current;
    return () => {
      Object.keys(sockets).forEach(tabId => sockets[tabId]?.disconnect());
      Object.keys(terminals).forEach(tabId => terminals[tabId]?.cleanup());
    };
  }, []);

  return (
    <div className={`flex min-h-[calc(100vh-60px)] transition-all duration-200 ${theme === 'dark' ? 'bg-slate-900 text-slate-100' : 'bg-gray-100 text-gray-900'}`}>
      {/* 사이드바 */}
      <div className={`fixed left-0 top-[60px] bottom-0 w-[280px] flex flex-col z-[100] shadow-lg transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${theme === 'dark' ? 'bg-slate-800 border-r border-slate-700' : 'bg-white border-r border-gray-200'}`}>
        <div className={`flex justify-between items-center p-4 border-b ${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className={`flex gap-1 p-[3px] rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`}>
            <button
              className={`px-3.5 py-2 text-[13px] font-medium rounded-md transition-all duration-200 ${sidebarTab === 'connections' ? (theme === 'dark' ? 'bg-slate-600 text-white shadow' : 'bg-white text-gray-900 shadow') : (theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900')}`}
              onClick={() => setSidebarTab('connections')}
            >
              연결
            </button>
            <button
              className={`px-3.5 py-2 text-[13px] font-medium rounded-md transition-all duration-200 ${sidebarTab === 'keys' ? (theme === 'dark' ? 'bg-slate-600 text-white shadow' : 'bg-white text-gray-900 shadow') : (theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900')}`}
              onClick={() => setSidebarTab('keys')}
            >
              SSH 키
            </button>
          </div>
          <button className={`text-xl px-2 py-1 rounded-md transition-all duration-200 ${theme === 'dark' ? 'text-slate-400 hover:bg-slate-700 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-900'}`} onClick={() => setSidebarOpen(false)}>x</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {sidebarTab === 'connections' ? (
            <div>
              <div className={`flex justify-between items-center py-2 mb-3 border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                <span className={`text-xs uppercase tracking-wide font-semibold ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>저장된 연결</span>
                <button className="w-[26px] h-[26px] rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center text-lg transition-all duration-200 hover:scale-105" onClick={() => setConnectionModal(true)}>+</button>
              </div>
              {savedConnections.length === 0 ? (
                <div className={`text-center py-8 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>저장된 연결이 없습니다</div>
              ) : (
                savedConnections.map(conn => (
                  <div key={conn.id} className={`group flex items-center p-3 rounded-lg mb-1.5 cursor-pointer transition-all duration-200 border border-transparent ${theme === 'dark' ? 'bg-slate-700 hover:bg-slate-600 hover:border-slate-500' : 'bg-gray-50 hover:bg-gray-100 hover:border-gray-300'}`}>
                    <div className="flex-1 flex items-center gap-3 min-w-0" onClick={() => connectFromSaved(conn)}>
                      <span className="text-lg flex-shrink-0">🔗</span>
                      <div className="flex flex-col min-w-0 gap-0.5">
                        <span className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>{conn.name}</span>
                        <span className={`text-xs truncate ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>{conn.username}@{conn.host}</span>
                      </div>
                    </div>
                    <button className={`opacity-0 group-hover:opacity-100 text-lg px-2 py-1 rounded-md transition-all duration-200 ${theme === 'dark' ? 'text-slate-400 hover:text-red-400 hover:bg-red-900/30' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`} onClick={() => deleteConnection(conn.id)}>x</button>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div>
              <div className={`flex justify-between items-center py-2 mb-3 border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                <span className={`text-xs uppercase tracking-wide font-semibold ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>SSH 키</span>
                <button className="w-[26px] h-[26px] rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center text-lg transition-all duration-200 hover:scale-105" onClick={() => setKeyModal(true)}>+</button>
              </div>
              {sshKeys.length === 0 ? (
                <div className={`text-center py-8 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>SSH 키가 없습니다</div>
              ) : (
                sshKeys.map(key => (
                  <div key={key.id} className={`group flex items-center p-3 rounded-lg mb-1.5 cursor-pointer transition-all duration-200 border border-transparent ${theme === 'dark' ? 'bg-slate-700 hover:bg-slate-600 hover:border-slate-500' : 'bg-gray-50 hover:bg-gray-100 hover:border-gray-300'}`}>
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <span className="text-lg flex-shrink-0">🔑</span>
                      <div className="flex flex-col min-w-0 gap-0.5">
                        <span className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>{key.name}</span>
                        <span className={`text-xs truncate ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>{key.key_type.toUpperCase()}</span>
                      </div>
                    </div>
                    <button className={`opacity-0 group-hover:opacity-100 text-lg px-2 py-1 rounded-md transition-all duration-200 ${theme === 'dark' ? 'text-slate-400 hover:text-red-400 hover:bg-red-900/30' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`} onClick={() => deleteKey(key.id)}>x</button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className={`p-4 border-t ${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
          <button className={`w-full p-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all duration-200 ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 hover:bg-slate-600 hover:border-slate-500' : 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-100 hover:border-gray-400'}`} onClick={openLocalTerminal}>
            💻 로컬 터미널
          </button>
        </div>
      </div>

      {/* 메인 영역 */}
      <div className={`flex-1 flex flex-col p-6 max-w-full overflow-hidden transition-all duration-300 ${sidebarOpen ? 'ml-[280px]' : 'ml-0'}`}>
        <div className="flex items-center gap-4 mb-5">
          {!sidebarOpen && (
            <button className={`px-3.5 py-2.5 rounded-lg text-base transition-all duration-200 ${theme === 'dark' ? 'bg-slate-800 border border-slate-700 text-slate-100 hover:bg-slate-700' : 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-100'}`} onClick={() => setSidebarOpen(true)}>☰</button>
          )}
          <h1 className={`flex-1 text-[28px] font-bold tracking-tight ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>웹 터미널</h1>
          <div className="flex gap-2 items-center">
            <button
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 ${theme === 'dark' ? 'bg-blue-500/20 text-slate-100 hover:bg-blue-500 hover:text-white' : 'bg-blue-50 text-gray-900 hover:bg-blue-500 hover:text-white'}`}
              onClick={toggleTheme}
              aria-label={theme === 'light' ? '다크 모드로 전환' : '라이트 모드로 전환'}
            >
              {theme === 'light' ? <MoonIcon /> : <SunIcon />}
            </button>
            <button className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium transition-all duration-200" onClick={() => setConnectionModal(true)}>
              + 새 터미널
            </button>
          </div>
        </div>

        {error && (
          <div className={`flex justify-between items-center px-4 py-3.5 rounded-xl mb-5 text-sm ${theme === 'dark' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-500'}`}>
            <span>{error}</span>
            <button className="text-xl px-1 opacity-70 hover:opacity-100 transition-opacity" onClick={() => setError(null)}>x</button>
          </div>
        )}

        {tabs.length === 0 ? (
          <div className={`flex-1 flex flex-col items-center justify-center text-center p-12 rounded-2xl shadow-md ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`}>
            <div className="text-6xl mb-5">💻</div>
            <h2 className={`text-2xl font-semibold mb-2 ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>터미널이 없습니다</h2>
            <p className={`mb-7 text-base ${theme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>새 터미널을 열어 시작하세요.</p>
            <button className="px-7 py-3.5 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-[15px] font-medium transition-all duration-200" onClick={() => setConnectionModal(true)}>
              + 새 터미널
            </button>
          </div>
        ) : (
          <div className={`flex-1 flex flex-col rounded-2xl overflow-hidden shadow-md ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`} ref={containerRef}>
            <div className={`flex overflow-x-auto px-2 pt-2 gap-1 ${theme === 'dark' ? 'bg-slate-800 border-b border-slate-700' : 'bg-gray-50 border-b border-gray-200'}`}>
              {tabs.map(tab => (
                <div
                  key={tab.id}
                  className={`flex items-center gap-2 px-3.5 py-2.5 rounded-t-lg cursor-pointer transition-all duration-200 min-w-[120px] max-w-[200px] text-[13px] border border-b-0 ${activeTab === tab.id ? 'bg-[#1e1e1e] text-white border-[#1e1e1e]' : (theme === 'dark' ? 'bg-slate-700 text-slate-400 border-transparent hover:bg-slate-600 hover:text-white' : 'bg-gray-200 text-gray-500 border-transparent hover:bg-gray-300 hover:text-gray-900')}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="text-sm">{tab.type === 'local' ? '💻' : '🔗'}</span>
                  <span className="flex-1 truncate font-medium">{tab.name}</span>
                  <span className="text-[10px]">
                    {tab.status === 'connecting' && '⏳'}
                    {tab.status === 'connected' && '🟢'}
                    {tab.status === 'disconnected' && '🔴'}
                    {tab.status === 'error' && '⚠️'}
                    {tab.status === 'exited' && '⬛'}
                  </span>
                  <button className={`text-base px-1.5 py-0.5 rounded opacity-60 hover:opacity-100 transition-all duration-200 ${activeTab === tab.id ? 'hover:bg-white/20' : 'hover:bg-red-500/20 hover:text-red-500'}`} onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}>x</button>
                </div>
              ))}
              <button className={`px-3.5 py-2 text-xl rounded-lg transition-all duration-200 ${theme === 'dark' ? 'text-slate-400 hover:text-blue-400 hover:bg-blue-500/20' : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'}`} onClick={() => setConnectionModal(true)}>+</button>
            </div>

            <div className="flex-1 relative bg-[#1e1e1e] min-h-[400px]">
              {tabs.map(tab => (
                <div key={tab.id} className={`absolute inset-0 ${activeTab === tab.id ? 'block' : 'hidden'}`}>
                  <div id={`terminal-${tab.id}`} className="w-full h-full p-3 box-border" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 연결 모달 */}
      {connectionModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fade-in" onClick={() => setConnectionModal(false)}>
          <div className={`w-full max-w-[480px] max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-scale-in ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex justify-between items-center px-6 py-5 border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>새 터미널</h2>
              <button className={`w-8 h-8 rounded-full flex items-center justify-center text-xl transition-all duration-200 ${theme === 'dark' ? 'bg-slate-700 text-slate-400 hover:bg-slate-600 hover:text-white' : 'bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-900'}`} onClick={() => setConnectionModal(false)}>x</button>
            </div>
            <div className="p-6">
              <div className="flex gap-3 mb-6">
                <button
                  className={`flex-1 p-4 rounded-xl text-sm font-medium transition-all duration-200 border-2 ${connectionForm.type === 'local' ? 'border-blue-500 bg-blue-500/10 text-blue-500' : (theme === 'dark' ? 'border-slate-600 bg-slate-700 text-slate-400 hover:border-blue-500 hover:text-white' : 'border-gray-300 bg-gray-100 text-gray-500 hover:border-blue-500 hover:text-gray-900')}`}
                  onClick={() => setConnectionForm(prev => ({ ...prev, type: 'local' }))}
                >
                  💻 로컬 터미널
                </button>
                <button
                  className={`flex-1 p-4 rounded-xl text-sm font-medium transition-all duration-200 border-2 ${connectionForm.type === 'ssh' ? 'border-blue-500 bg-blue-500/10 text-blue-500' : (theme === 'dark' ? 'border-slate-600 bg-slate-700 text-slate-400 hover:border-blue-500 hover:text-white' : 'border-gray-300 bg-gray-100 text-gray-500 hover:border-blue-500 hover:text-gray-900')}`}
                  onClick={() => setConnectionForm(prev => ({ ...prev, type: 'ssh' }))}
                >
                  🔗 SSH 연결
                </button>
              </div>

              {connectionForm.type === 'local' ? (
                <div className={`text-center p-6 rounded-xl ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className={`text-[15px] mb-3 ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>서버의 로컬 셸에 접속합니다.</p>
                  <p className="text-amber-500 text-[13px]">⚠️ 이 기능은 제한된 환경에서 실행됩니다.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <label className={`text-[13px] font-medium ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>연결 이름</label>
                    <input
                      type="text"
                      placeholder="예: My Server"
                      value={connectionForm.name}
                      onChange={e => setConnectionForm(prev => ({ ...prev, name: e.target.value }))}
                      className={`px-3.5 py-3 rounded-lg text-[15px] transition-all duration-200 outline-none ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:bg-slate-800' : 'bg-gray-100 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white'} focus:ring-2 focus:ring-blue-500/20`}
                    />
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1 flex flex-col gap-2">
                      <label className={`text-[13px] font-medium ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>호스트 *</label>
                      <input
                        type="text"
                        placeholder="예: 192.168.1.100"
                        value={connectionForm.host}
                        onChange={e => setConnectionForm(prev => ({ ...prev, host: e.target.value }))}
                        className={`px-3.5 py-3 rounded-lg text-[15px] transition-all duration-200 outline-none ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:bg-slate-800' : 'bg-gray-100 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white'} focus:ring-2 focus:ring-blue-500/20`}
                      />
                    </div>
                    <div className="w-[100px] flex flex-col gap-2">
                      <label className={`text-[13px] font-medium ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>포트</label>
                      <input
                        type="number"
                        value={connectionForm.port}
                        onChange={e => setConnectionForm(prev => ({ ...prev, port: parseInt(e.target.value) || 22 }))}
                        className={`px-3.5 py-3 rounded-lg text-[15px] transition-all duration-200 outline-none ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 focus:border-blue-500 focus:bg-slate-800' : 'bg-gray-100 border border-gray-300 text-gray-900 focus:border-blue-500 focus:bg-white'} focus:ring-2 focus:ring-blue-500/20`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className={`text-[13px] font-medium ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>사용자명 *</label>
                    <input
                      type="text"
                      placeholder="예: ubuntu"
                      value={connectionForm.username}
                      onChange={e => setConnectionForm(prev => ({ ...prev, username: e.target.value }))}
                      className={`px-3.5 py-3 rounded-lg text-[15px] transition-all duration-200 outline-none ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:bg-slate-800' : 'bg-gray-100 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white'} focus:ring-2 focus:ring-blue-500/20`}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className={`text-[13px] font-medium ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>비밀번호</label>
                    <input
                      type="password"
                      placeholder="비밀번호 입력"
                      value={connectionForm.password}
                      onChange={e => setConnectionForm(prev => ({ ...prev, password: e.target.value }))}
                      className={`px-3.5 py-3 rounded-lg text-[15px] transition-all duration-200 outline-none ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:bg-slate-800' : 'bg-gray-100 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white'} focus:ring-2 focus:ring-blue-500/20`}
                    />
                  </div>
                  <div className="flex items-center gap-2.5 pt-1">
                    <input
                      type="checkbox"
                      id="saveConnection"
                      checked={connectionForm.saveConnection}
                      onChange={e => setConnectionForm(prev => ({ ...prev, saveConnection: e.target.checked }))}
                      className="w-[18px] h-[18px] cursor-pointer accent-blue-500"
                    />
                    <label htmlFor="saveConnection" className={`text-sm cursor-pointer ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>연결 정보 저장</label>
                  </div>
                </div>
              )}
            </div>
            <div className={`flex justify-end gap-3 px-6 py-5 border-t rounded-b-2xl ${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
              <button className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 hover:bg-slate-600' : 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-100'}`} onClick={() => setConnectionModal(false)}>취소</button>
              <button
                className="px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full text-sm font-medium transition-all duration-200"
                onClick={connectionForm.type === 'local' ? openLocalTerminal : openSSHConnection}
                disabled={isConnecting}
              >
                {isConnecting ? '연결 중...' : '연결'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SSH 키 생성 모달 */}
      {keyModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fade-in" onClick={() => setKeyModal(false)}>
          <div className={`w-full max-w-[380px] max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-scale-in ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex justify-between items-center px-6 py-5 border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>SSH 키 생성</h2>
              <button className={`w-8 h-8 rounded-full flex items-center justify-center text-xl transition-all duration-200 ${theme === 'dark' ? 'bg-slate-700 text-slate-400 hover:bg-slate-600 hover:text-white' : 'bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-900'}`} onClick={() => setKeyModal(false)}>x</button>
            </div>
            <div className="p-6">
              <div className="flex flex-col gap-2 mb-4">
                <label className={`text-[13px] font-medium ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>키 이름 *</label>
                <input
                  type="text"
                  placeholder="예: my-server-key"
                  value={keyForm.name}
                  onChange={e => setKeyForm(prev => ({ ...prev, name: e.target.value }))}
                  className={`px-3.5 py-3 rounded-lg text-[15px] transition-all duration-200 outline-none ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:bg-slate-800' : 'bg-gray-100 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white'} focus:ring-2 focus:ring-blue-500/20`}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={`text-[13px] font-medium ${theme === 'dark' ? 'text-slate-100' : 'text-gray-900'}`}>키 타입</label>
                <select
                  value={keyForm.keyType}
                  onChange={e => setKeyForm(prev => ({ ...prev, keyType: e.target.value }))}
                  className={`px-3.5 py-3 rounded-lg text-[15px] transition-all duration-200 outline-none ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 focus:border-blue-500 focus:bg-slate-800' : 'bg-gray-100 border border-gray-300 text-gray-900 focus:border-blue-500 focus:bg-white'} focus:ring-2 focus:ring-blue-500/20`}
                >
                  <option value="rsa" className={theme === 'dark' ? 'bg-slate-800 text-slate-100' : ''}>RSA (4096 bit)</option>
                  <option value="ed25519" className={theme === 'dark' ? 'bg-slate-800 text-slate-100' : ''}>Ed25519</option>
                </select>
              </div>
            </div>
            <div className={`flex justify-end gap-3 px-6 py-5 border-t rounded-b-2xl ${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
              <button className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 ${theme === 'dark' ? 'bg-slate-700 border border-slate-600 text-slate-100 hover:bg-slate-600' : 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-100'}`} onClick={() => setKeyModal(false)}>취소</button>
              <button className="px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full text-sm font-medium transition-all duration-200" onClick={generateKey} disabled={loading}>
                {loading ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* xterm 스타일 */}
      <style>{`
        .xterm { height: 100%; }
        .xterm-viewport { overflow-y: auto !important; }
      `}</style>
    </div>
  );
};

export default TerminalPage;
